import React, { useCallback, useMemo } from 'react'
import FlexLayout from 'components/Layout/Flex'
import { useFarms, usePriceVvsUsdc, useTokenPricesUsdc } from 'state/farms/hooks'
import FarmCard from 'views/Farms/components/FarmCard/FarmCard'
import { FarmWithStakedValue } from 'views/Farms/interfaces'
import { getDisplayApr } from 'views/Farms/utils'
import BigNumber from 'bignumber.js'
import { getFarmAprByVersion } from 'utils/apr'
import { DeserializedFarm } from 'state/types'
import { getAddress } from 'utils/addressHelpers'
import { getBalanceAmount } from 'utils/formatBalance'
import { useUserVvsBoost } from 'state/xVvs/hooks'
import { useGetBoostedFarms } from 'hooks/boost-farm/useFarmBoostContract'
import { useWeb3React } from '@web3-react/core'
import SkeletonCard from 'views/Pool/components/SkeletonCard'
import { useTranslation } from 'contexts/Localization'
import { Text } from 'vvs-uikit'
import { getLpUsdcPrice } from 'utils/farmsPriceHelpers'

const MyFarms = () => {
  const { t } = useTranslation()
  const { data: farmsLP, userDataLoaded } = useFarms()
  const { account } = useWeb3React()
  const vvsPrice = usePriceVvsUsdc()
  // const activeFarms = farmsLP.filter((farm) => farm.pid !== 0 && farm.multiplier !== '0X' && !isArchivedPid(farm.pid))
  const farms = farmsLP.filter((farm) => farm.pid !== 0) // exclude VVS (pid:0) and include finished farms
  const tokenPrices = useTokenPricesUsdc(farms)
  const stakedOnlyFarms = farms.filter(
    (farm) => farm.userData && new BigNumber(farm.userData.stakedBalance).isGreaterThan(0),
  )

  const { data: boostedFarms } = useGetBoostedFarms()
  const { boostData, userDataLoaded: boostDataLoaded } = useUserVvsBoost()
  const { stakes: xVVSStakes } = boostData
  const xVVSStaked = xVVSStakes?.filter((stake) => stake.active).length > 0

  const farmsList = useCallback(
    (farmsToDisplay: DeserializedFarm[]): FarmWithStakedValue[] => {
      const farmsToDisplayWithAPR: FarmWithStakedValue[] = farmsToDisplay.map((farm) => {
        if (!farm.lpTotalInQuoteToken || !farm.quoteTokenPriceUsdc) {
          return farm
        }
        const totalLiquidity = new BigNumber(farm.lpTotalInQuoteToken).times(farm.quoteTokenPriceUsdc)

        const lpTokenPrice: BigNumber = getLpUsdcPrice(farm)
        const stakedLiquidityInUSD =
          farm.version === 2 && farm.migration
            ? getBalanceAmount(farm.lpTokenAmountInCraftsmanV2).times(lpTokenPrice)
            : totalLiquidity

        const { vvsRewardsApr, tokenRewardsApr, lpRewardsApr } = getFarmAprByVersion(
          farm.poolWeight,
          vvsPrice,
          totalLiquidity,
          getAddress(farm.lpAddresses),
          farm.version,
          farm.rewardData,
          stakedLiquidityInUSD,
          tokenPrices,
        )

        const isBoosted = boostedFarms?.includes(farm.pid)

        return {
          ...farm,
          isBoosted,
          apr: tokenRewardsApr,
          vvsRewardsApr,
          lpRewardsApr,
          liquidity: stakedLiquidityInUSD,
        }
      })

      return farmsToDisplayWithAPR
    },
    [vvsPrice, tokenPrices, boostedFarms],
  )

  const chosenFarmsMemoized = useMemo(() => {
    let chosenFarms = []

    chosenFarms = farmsList(stakedOnlyFarms)

    return chosenFarms
  }, [stakedOnlyFarms, farmsList])

  const renderBody = () => {
    if (!account) {
      return (
        <Text color="textSubtle" textAlign="center">
          {t('Connect to a wallet to view your staked Crystal Farms.')}
        </Text>
      )
    }
    if (!userDataLoaded || !boostDataLoaded) {
      return [0, 1, 2].map((el) => <SkeletonCard key={el} />)
    }
    if (chosenFarmsMemoized.length > 0) {
      return chosenFarmsMemoized.map((farm) => (
        <FarmCard
          key={farm.pid}
          farm={farm}
          displayApr={getDisplayApr(farm.apr, farm.lpRewardsApr)}
          vvsPrice={vvsPrice}
          account={account}
          removed={false}
          farms={farmsLP}
          xVVSStaked={xVVSStaked}
          onDashboard
        />
      ))
    }
    return (
      <Text color="textSubtle" textAlign="center">
        {t(
          'No records. Stake your liquidity in a Farm for it to appear here. Check the My Liquidity tab to see any unstaked LP.',
        )}
      </Text>
    )
  }

  return (
    <div>
      <FlexLayout>{renderBody()}</FlexLayout>
    </div>
  )
}

export default MyFarms
